import { useEffect, useCallback } from 'react';
import {
  Breadcrumbs,
  Link,
  Page,
  PageBody,
  PageBreadcrumbs,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Banner,
  Stack,
  Spinner,
  Text,
  Box,
  InlineStack,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import { AuditLogsTable } from './audit-logs-table';
import { ExportAuditLogsModal, useExportAuditLogsModal } from './export-audit-logs-modal';
import type { AuditLogResponse } from '../types';
import { AppFooter } from '@/components/app-footer';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useAuditLogsData } from '../hooks/use-audit-logs-data';
import { Helmet } from 'react-helmet-async';

interface AuditLogsPageContainerProps {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
  /** Workspace ID for exports link. For workspace context, this should match contextId */
  workspaceId: number | null;
  breadcrumbs: Array<{ text: string; href?: string }>;
  pageTitle: string;
  pageDescription: React.ReactNode;
  backUrlBase: string;
  feedbackFeature: string;
}

export const AuditLogsPageContainer: React.FC<AuditLogsPageContainerProps> = ({
  context,
  contextId,
  workspaceId,
  breadcrumbs,
  pageTitle,
  pageDescription,
  backUrlBase,
  feedbackFeature,
}) => {
  const { track } = useAnalytics();
  const navigate = useSafeNavigate();
  const showFeedbackModal = useCustomerFeedback();
  const exportModal = useExportAuditLogsModal();

  const {
    filters,
    uiObjectType,
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    objectTypesQuery,
    workspaces,
    handleFiltersChange,
    handleSetUiObjectType,
    handleLoadUsers,
    handleLoadSegments,
    segments,
    isLoadingSegments,
    // Campaign props
    campaigns,
    isLoadingCampaigns,
    isFetchingMoreCampaigns,
    hasMoreCampaigns,
    onLoadMoreCampaigns,
    onCampaignSearch,
    campaignSearchString,
    // Newsletter props
    newsletters,
    isLoadingNewsletters,
    // Transactional message props
    transactionalMessages,
    isLoadingTransactionalMessages,
    // Legacy props
    isLoadingUsers,
    messages,
    isLoadingMessages,
    onLoadMessages,
    roleDefinitions,
  } = useAuditLogsData({
    context,
    contextId,
  });

  // Track page view (filters computed inside to avoid duplicate tracking on filter changes)
  const trackPageView = useCallback(() => {
    // Check if any filters are applied (excluding pagination/sorting)
    const hasFilters = !!(
      filters.user_id != null ||
      !isEmptyString(filters.ip_addr) ||
      !isEmptyString(filters.object_type) ||
      !isEmptyString(filters.object_id) ||
      !isEmptyString(filters.attribute_name) ||
      !isEmptyString(filters.action) ||
      !isEmptyString(filters.start_time) ||
      !isEmptyString(filters.end_time)
    );

    track('auditLogs', 'viewed', {
      contextType: context,
      workspaceId: contextId,
      hasFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent duplicate tracking
  }, [context, contextId, track]);

  usePageViewTracking(trackPageView, contextId !== null);

  // Eagerly load users on mount to avoid popover closing issue
  useEffect(() => {
    handleLoadUsers();
  }, [handleLoadUsers]);

  const AuditLogsPageLayout = ({
    children,
    customBreadcrumbs,
  }: {
    children: React.ReactNode;
    customBreadcrumbs?: Array<{ text: string; href?: string }>;
  }) => (
    <>
      <Helmet>
        <title>{pageTitle} | Customer.io</title>
      </Helmet>
      <Page style={{ width: '100%' }}>
        <PageBreadcrumbs>
          <Breadcrumbs breadcrumbs={customBreadcrumbs ?? breadcrumbs} />
        </PageBreadcrumbs>
        {children}
        <AppFooter />
      </Page>
    </>
  );

  // Handle deep link to specific audit log
  if (auditLogId != null) {
    if (singleLogQuery.isLoading) {
      return (
        <AuditLogsPageLayout>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Box p="800" textAlign="center">
              <Spinner />
              <Text mt="300">Loading audit log...</Text>
            </Box>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    if (singleLogQuery.error != null || singleLogQuery.data == null) {
      const singleLogBreadcrumbs = [...breadcrumbs, { text: `Audit Log #${auditLogId}` }];
      const errorMessage =
        singleLogQuery.error != null
          ? `Error loading audit log: ${
              singleLogQuery.error instanceof Error ? singleLogQuery.error.message : 'Unknown error'
            }`
          : `Audit log #${auditLogId} not found`;
      const errorDetails =
        'This audit log may not exist, may have been deleted, or you may not have permission to view it.';

      return (
        <AuditLogsPageLayout customBreadcrumbs={singleLogBreadcrumbs}>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Banner variant="error" title={errorMessage}>
              <Text>{errorDetails}</Text>
            </Banner>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    // Create a response object compatible with AuditLogsTable
    const deepLinkedResponse: AuditLogResponse = {
      data: [singleLogQuery.data.body.data],
      users: singleLogQuery.data.body.user != null ? [singleLogQuery.data.body.user] : [],
      pagination: {
        limit: 1,
        sort_order: 'desc',
        next_cursor: 0,
      },
    };

    const singleLogBreadcrumbs = [...breadcrumbs, { text: `Audit Log #${auditLogId}` }];

    return (
      <AuditLogsPageLayout customBreadcrumbs={singleLogBreadcrumbs}>
        <PageHeader>
          <PageHeaderTitle>Audit Log #{auditLogId}</PageHeaderTitle>
          <PageHeaderDescription>Viewing a single audit log entry.</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Stack gap="400">
            <Box>
              <Link
                icon="chevron-left"
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(backUrlBase);
                }}
              >
                Back to All Logs
              </Link>
            </Box>
            <AuditLogsTable
              data={deepLinkedResponse}
              isLoading={false}
              error={undefined}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSetUiObjectType={handleSetUiObjectType}
              uiObjectType={uiObjectType}
              highlightedLogId={auditLogId.toString()}
              context={context}
              contextId={contextId ?? 0}
              allUsers={allUsersQuery.data?.body.users ?? []}
              isLoadingUsers={isLoadingUsers}
              onLoadUsers={handleLoadUsers}
              campaigns={campaigns}
              isLoadingCampaigns={isLoadingCampaigns}
              isFetchingMoreCampaigns={isFetchingMoreCampaigns}
              hasMoreCampaigns={hasMoreCampaigns}
              onLoadMoreCampaigns={onLoadMoreCampaigns}
              onCampaignSearch={onCampaignSearch}
              campaignSearchString={campaignSearchString}
              segments={segments}
              isLoadingSegments={isLoadingSegments}
              onLoadSegments={handleLoadSegments}
              newsletters={newsletters}
              isLoadingNewsletters={isLoadingNewsletters}
              transactionalMessages={transactionalMessages}
              isLoadingTransactionalMessages={isLoadingTransactionalMessages}
              workspaces={workspaces}
              roleDefinitions={roleDefinitions}
              objectTypes={objectTypesQuery.data ?? []}
              messages={messages}
              isLoadingMessages={isLoadingMessages}
              onLoadMessages={onLoadMessages}
            />
          </Stack>
        </PageBody>
      </AuditLogsPageLayout>
    );
  }

  // Get current object type filter for export
  const currentObjectType = filters.object_type;

  // Normal audit logs list view
  return (
    <AuditLogsPageLayout>
      <PageHeader>
        <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
        <PageHeaderActions>
          <InlineStack gap="100">
            <Button variant="secondary" icon="export" onClick={() => exportModal.show()}>
              Export CSV
            </Button>
            <Button
              variant="tertiary"
              icon="slack"
              onClick={() => {
                void showFeedbackModal({ feature: feedbackFeature });
              }}
            >
              Feedback
            </Button>
          </InlineStack>
        </PageHeaderActions>
        <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
      </PageHeader>
      <ExportAuditLogsModal
        isOpen={exportModal.isOpen}
        onClose={() => exportModal.close()}
        context={context}
        contextId={contextId ?? null}
        workspaceId={workspaceId}
        workspaces={workspaces}
        currentObjectType={currentObjectType}
      />
      <PageBody>
        {/* Audit Logs Table */}
        <AuditLogsTable
          data={accumulatedLogs}
          isLoading={auditLogsQuery?.isLoading && filters.cursor == null}
          error={auditLogsQuery?.error}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSetUiObjectType={handleSetUiObjectType}
          uiObjectType={uiObjectType}
          highlightedLogId={auditLogId != null ? String(auditLogId) : undefined}
          context={context}
          contextId={contextId ?? 0}
          allUsers={allUsersQuery.data?.body.users ?? []}
          isLoadingUsers={isLoadingUsers}
          onLoadUsers={handleLoadUsers}
          campaigns={campaigns}
          isLoadingCampaigns={isLoadingCampaigns}
          isFetchingMoreCampaigns={isFetchingMoreCampaigns}
          hasMoreCampaigns={hasMoreCampaigns}
          onLoadMoreCampaigns={onLoadMoreCampaigns}
          onCampaignSearch={onCampaignSearch}
          campaignSearchString={campaignSearchString}
          segments={segments}
          isLoadingSegments={isLoadingSegments}
          onLoadSegments={handleLoadSegments}
          newsletters={newsletters}
          isLoadingNewsletters={isLoadingNewsletters}
          transactionalMessages={transactionalMessages}
          isLoadingTransactionalMessages={isLoadingTransactionalMessages}
          workspaces={workspaces}
          roleDefinitions={roleDefinitions}
          objectTypes={objectTypesQuery.data ?? []}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          onLoadMessages={onLoadMessages}
        />
      </PageBody>
    </AuditLogsPageLayout>
  );
};
